import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";

import socket from "../socket";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  applyMove,
  applyGameRestarted,
  applyRematchVote,
  applyMatchForfeited,
  applyTimerUpdate,
  applyTurnTimeout,
  applyResumeVote,
  resetMultiplayer,
  setError,
  setRoomUpdated,
  setConnected,
  setRoomRejoined,
  selectMpRoom,
  selectMpRole,
  selectMpMyColor,
  selectMpIsConnected,
  selectMpRematchVotes,
  selectMultiplayer,
  RoomState,
} from "../store/multiplayerSlice";
import { recordWin, recordLoss, recordTie } from "../store/profileSlice";
import { useVoiceChat } from "../hooks/useVoiceChat";
import SpectatorList from "../components/multiplayer/SpectatorList";
import VoiceChat from "../components/multiplayer/VoiceChat";
import RoomInfo from "../components/multiplayer/RoomInfo";
import ShareInviteBtn from "../components/multiplayer/ShareInviteBtn";
import PlayerAvatar from "../components/multiplayer/PlayerAvatar";
import { useFarcaster } from "../contexts/FarcasterContext";
import { media } from "../styles/GlobalStyles";

import useWindowWidth from "../hooks/useWindowWidth";
import { ReactComponent as WhiteLayoutLarge } from "../assets/images/board-layer-white-large.svg";
import { ReactComponent as BlackLayoutLarge } from "../assets/images/board-layer-black-large.svg";
import { ReactComponent as WhiteLayoutSmall } from "../assets/images/board-layer-white-small.svg";
import { ReactComponent as BlackLayoutSmall } from "../assets/images/board-layer-black-small.svg";
import CounterRedLarge from "../assets/images/counter-red-large.svg";
import CounterYellowLarge from "../assets/images/counter-yellow-large.svg";
import CounterRedSmall from "../assets/images/counter-red-small.svg";
import CounterYellowSmall from "../assets/images/counter-yellow-small.svg";
import { getPlayerId } from "../helpers/identity";
import { APP_BASE_URL } from "../config";

// ─── Styled Components ────────────────────────────────────────────────────────

const PageWrapper = styled.div`
  min-height: 100vh;
  background: var(--color-dark-purple);
  display: flex;
  flex-direction: column;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.6rem 2.4rem;
  background: rgba(0, 0, 0, 0.2);
  gap: 1.2rem;
  flex-wrap: wrap;

  ${media.phone} {
    padding: 1rem 1.2rem;
    gap: 0.8rem;
  }
`;

const QuitBtn = styled.button`
  background: var(--color-red);
  border: 2px solid var(--color-black);
  box-shadow: 0 0.4rem 0 var(--color-black);
  border-radius: 1rem;
  padding: 0.6rem 1.6rem;
  color: var(--color-white);
  font-size: var(--font-size-heading-xs);
  font-family: inherit;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    box-shadow: 0 0.4rem 0 var(--color-dark-purple);
  }
  &:active {
    transform: translateY(2px);
    box-shadow: 0 2px 0 var(--color-dark-purple);
  }
`;

const PauseBtn = styled(QuitBtn)`
  background: var(--color-yellow);
  color: var(--color-black);
`;

/**
 * Outer game area: on desktop, three columns (P1 card | board | P2 card),
 * matching the CPU vs Player layout. On mobile, collapses to a single
 * centered column (board in the middle, player cards above & below).
 */
const GameArea = styled.main`
  flex: 1;
  display: grid;
  grid-template-columns: 14rem 1fr 14rem;
  grid-template-areas:
    ". . ."
    "p1 board p2"
    ". info ."
    ". extras .";
  align-items: center;
  justify-items: center;
  padding: 2rem;
  gap: 0 2.4rem;

  ${media.phone} {
    grid-template-columns: 1fr;
    grid-template-areas:
      "top-info"
      "board"
      "info"
      "extras";
    padding: 1rem;
    gap: 0.8rem;
  }
`;

const P1Slot = styled.div`
  grid-area: p1;
  align-self: center;

  ${media.phone} {
    display: none;
  }
`;

const P2Slot = styled.div`
  grid-area: p2;
  align-self: center;

  ${media.phone} {
    display: none;
  }
`;

const BoardSlot = styled.div`
  grid-area: board;
`;

const InfoSlot = styled.div`
  grid-area: info;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.8rem;
  width: 100%;
`;

const ExtrasSlot = styled.div`
  grid-area: extras;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  width: 100%;
`;

/** Mobile-only row: shows both player cards above the board */
const MobilePlayersRow = styled.div`
  display: none;
  grid-area: top-info;

  ${media.phone} {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1.2rem;
    width: 100%;
  }
`;

const PlayerCard = styled.div<{ color: "red" | "yellow"; isActive: boolean }>`
  background: ${({ color }) =>
    color === "red" ? "var(--color-red)" : "var(--color-yellow)"};
  border: 3px solid var(--color-black);
  box-shadow: ${({ isActive }) =>
    isActive
      ? "0 0.6rem 0 var(--color-white)"
      : "0 0.6rem 0 var(--color-black)"};
  border-radius: 1.6rem;
  padding: 1rem 1.6rem;
  text-align: center;
  min-width: 12rem;
  transition: box-shadow 0.2s;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.6rem;
`;

const PlayerName = styled.p<{ color: "red" | "yellow" }>`
  font-size: var(--font-size-heading-xs);
  font-weight: bold;
  text-transform: uppercase;
  color: ${({ color }) =>
    color === "yellow" ? "var(--color-black)" : "var(--color-white)"};
`;

const PlayerScore = styled.p<{ color: "red" | "yellow" }>`
  font-size: var(--font-size-heading-l);
  font-weight: bold;
  line-height: 1;
  color: ${({ color }) =>
    color === "yellow" ? "var(--color-black)" : "var(--color-white)"};
`;

const TurnIndicator = styled.div`
  font-size: 1.4rem;
  color: var(--color-white);
  opacity: 0.75;
  text-align: center;
  text-transform: uppercase;
`;

const TimerBadge = styled.div<{ low: boolean }>`
  background: ${({ low }) => (low ? "var(--color-red)" : "var(--color-white)")};
  border: 3px solid var(--color-black);
  border-radius: 1.2rem;
  padding: 0.6rem 1.6rem;
  font-size: var(--font-size-heading-m);
  font-weight: bold;
  color: var(--color-black);
  text-align: center;
  transition: background 0.3s;
`;

/** Timer + turn row — visible on desktop, hidden on mobile (mobile uses MobilePlayersRow) */
const DesktopTimerRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;

  ${media.phone} {
    display: none;
  }
`;

// ── Board ─────────────────────────────────────────────────────────────────────

const BoardOuter = styled.div`
  position: relative;
  width: 63.2rem;
  height: 58.4rem;

  svg {
    position: absolute;
    top: 0;
    left: 0;
    pointer-events: none;
  }
  .board-white {
    z-index: 4;
  }
  .board-black {
    z-index: 2;
    top: 1rem !important;
  }

  ${media.phone} {
    width: 33.5rem;
    height: 31rem;
  }
`;

const CounterGridOverlay = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 7rem);
  grid-template-rows: repeat(6, 7rem);
  padding: 1.7rem 1.7rem 0 1.7rem;
  gap: 1.8rem;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 3;

  ${media.phone} {
    grid-template-columns: repeat(7, 4.1rem);
    grid-template-rows: repeat(6, 4.1rem);
    padding: 0.8rem 0.8rem 0 0.8rem;
    gap: 0.55rem;
  }
`;

const CounterCell = styled.div`
  width: 7rem;
  height: 7rem;

  ${media.phone} {
    width: 4.1rem;
    height: 4.1rem;
  }
`;

const CounterImg = styled(motion.div)<{ $src: string }>`
  width: 100%;
  height: 100%;
  background-image: url(${({ $src }) => $src});
  background-repeat: no-repeat;
  background-size: cover;
`;

const ControlOverlay = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 7rem);
  padding: 0 1.7rem;
  gap: 1.8rem;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 6;

  ${media.phone} {
    grid-template-columns: repeat(7, 4.1rem);
    padding: 0 0.8rem;
    gap: 0.55rem;
  }
`;

const ColumnBtn = styled.div<{ canPlay: boolean }>`
  cursor: ${({ canPlay }) => (canPlay ? "pointer" : "default")};
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 0.4rem;
`;

const Pointer = styled(motion.div)<{ color: "red" | "yellow" }>`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  background: ${({ color }) =>
    color === "red" ? "var(--color-red)" : "var(--color-yellow)"};
  border: 2px solid var(--color-black);

  ${media.phone} {
    width: 1.4rem;
    height: 1.4rem;
  }
`;

// ── Result overlay ────────────────────────────────────────────────────────────

const ResultOverlay = styled(motion.div)`
  background: var(--color-white);
  border: 3px solid var(--color-black);
  box-shadow: 0 1rem 0 var(--color-black);
  border-radius: 2rem;
  padding: 2.4rem 3.2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.6rem;
  min-width: 22rem;
`;

const ResultTitle = styled.p`
  font-size: 1.4rem;
  text-transform: uppercase;
  color: var(--color-black);
  opacity: 0.6;
`;

const ResultWinner = styled.h2`
  font-size: var(--font-size-heading-m);
  text-transform: uppercase;
  color: var(--color-black);
`;

const PlayAgainBtn = styled.button`
  background: var(--color-dark-purple);
  border: 3px solid var(--color-black);
  box-shadow: 0 0.6rem 0 var(--color-black);
  border-radius: 1.6rem;
  padding: 1rem 2.4rem;
  color: var(--color-white);
  font-size: var(--font-size-heading-xs);
  font-family: inherit;
  font-weight: bold;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    box-shadow: 0 0.6rem 0 var(--color-purple);
  }
  &:active {
    transform: translateY(2px);
  }
`;

const WaitingBanner = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border-radius: 1.2rem;
  padding: 1.4rem 2.4rem;
  color: var(--color-white);
  font-size: var(--font-size-heading-xs);
  text-align: center;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1.2rem;
  flex-wrap: wrap;
`;

const SpectatorBanner = styled.div`
  background: rgba(121, 69, 255, 0.3);
  border: 2px solid var(--color-purple);
  border-radius: 1.2rem;
  padding: 0.8rem 1.6rem;
  color: var(--color-white);
  font-size: 1.4rem;
  text-align: center;
`;

const ErrorMsg = styled.div`
  background: rgba(253, 102, 135, 0.2);
  border: 2px solid var(--color-red);
  border-radius: 1.2rem;
  padding: 1rem 1.6rem;
  color: var(--color-white);
  font-size: 1.4rem;
  text-align: center;
`;

const PauseBanner = styled(WaitingBanner)`
  background: rgba(255, 206, 103, 0.22);
  border: 2px solid var(--color-yellow);
  color: var(--color-white);
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

type Counter = "red" | "yellow" | null;

const counterImages: Record<string, Record<string, string>> = {
  red: { large: CounterRedLarge, small: CounterRedSmall },
  yellow: { large: CounterYellowLarge, small: CounterYellowSmall },
};

// ─── Component ────────────────────────────────────────────────────────────────

const MultiplayerGame: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const windowWidth = useWindowWidth();
  const { user: farcasterUser, composeCast } = useFarcaster();

  const mpState = useAppSelector(selectMultiplayer);
  const room = useAppSelector(selectMpRoom);
  const role = useAppSelector(selectMpRole);
  const myColor = useAppSelector(selectMpMyColor);
  const isConnected = useAppSelector(selectMpIsConnected);
  const rematchVotes = useAppSelector(selectMpRematchVotes);

  const [hoveredCol, setHoveredCol] = useState<number | null>(null);
  const [canMove, setCanMove] = useState(true);
  const [reconnecting, setReconnecting] = useState(false);
  const [lastMove, setLastMove] = useState<{ row: number; col: number } | null>(
    null,
  );
  const statsRecordedRef = useRef(false);

  const voiceChat = useVoiceChat(room?.id ?? null);

  const size = windowWidth > 760 ? "large" : "small";
  const isSpectator = role === "spectator";
  const isMyTurn = room && !isSpectator && room.turn === myColor;
  const isPaused = !!room?.isPaused;

  // ── Record local stats when game ends ────────────────────────────────────
  useEffect(() => {
    if (!room?.winner || isSpectator || statsRecordedRef.current) return;
    statsRecordedRef.current = true;
    if (room.winner === "tie") {
      dispatch(recordTie());
    } else if (room.winner === role) {
      dispatch(recordWin());
    } else {
      dispatch(recordLoss());
    }
  }, [room?.winner, role, isSpectator, dispatch]);

  // Reset stats guard on new game
  useEffect(() => {
    if (!room?.winner) statsRecordedRef.current = false;
  }, [room?.winner]);

  // ── Reconnect on page reload AND on any socket reconnect mid-match ───────
  // FIX M2: rejoin must fire on every `socket.on("connect")`, not just mount.
  useEffect(() => {
    if (!mpState.roomId || !mpState.role || !mpState.myColor) return;

    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    let initialRejoinDone = false;

    const storedRole = mpState.role;
    const storedRoomId = mpState.roomId;

    const doRejoin = () => {
      dispatch(setConnected(true));
      if (storedRole !== "p1" && storedRole !== "p2") return;
      socket.emit("rejoin-room", {
        roomId: storedRoomId,
        role: storedRole,
        playerName:
          farcasterUser?.displayName ||
          farcasterUser?.username ||
          mpState.room?.players.find((p) => p.role === storedRole)?.name ||
          "Player",
        fid: farcasterUser?.fid,
        pfpUrl: farcasterUser?.pfpUrl,
        username: farcasterUser?.username,
        playerId: getPlayerId(),
      });
    };

    const startFallbackTimer = () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      fallbackTimer = setTimeout(() => {
        if (!initialRejoinDone) {
          dispatch(resetMultiplayer());
          navigate("/multiplayer");
        }
      }, 8000);
    };

    setReconnecting(true);
    startFallbackTimer();

    const handleRoomJoined = (data: {
      roomId: string;
      role: "p1" | "p2" | "spectator";
      color: "red" | "yellow" | null;
      roomState: RoomState;
    }) => {
      if (data.roomId !== storedRoomId) return;
      if (fallbackTimer) clearTimeout(fallbackTimer);
      initialRejoinDone = true;
      dispatch(setRoomRejoined(data));
      setReconnecting(false);
    };

    const handleRejoinError = ({ message }: { message: string }) => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      if (process.env.NODE_ENV !== "production") {
        console.warn("[multiplayer] rejoin failed:", message);
      }
      dispatch(resetMultiplayer());
      navigate("/multiplayer");
    };

    const handleDisconnect = () => {
      dispatch(setConnected(false));
    };

    // Re-rejoin every time the socket reconnects mid-match.
    const handleConnect = () => {
      dispatch(setConnected(true));
      doRejoin();
    };

    socket.on("room-joined", handleRoomJoined);
    socket.on("rejoin-error", handleRejoinError);
    socket.on("disconnect", handleDisconnect);
    socket.on("connect", handleConnect);

    if (socket.connected) {
      doRejoin();
    } else {
      socket.connect();
    }

    return () => {
      if (fallbackTimer) clearTimeout(fallbackTimer);
      socket.off("connect", handleConnect);
      socket.off("room-joined", handleRoomJoined);
      socket.off("rejoin-error", handleRejoinError);
      socket.off("disconnect", handleDisconnect);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Redirect if no room ──────────────────────────────────────────────────
  useEffect(() => {
    if (!room && !reconnecting) {
      navigate("/multiplayer");
    }
  }, [room, reconnecting, navigate]);

  // ── Socket event listeners ────────────────────────────────────────────────
  useEffect(() => {
    const onRoomUpdated = (roomState: RoomState) => {
      dispatch(setRoomUpdated(roomState));
    };

    const onMoveMade = (data: {
      col: number;
      row: number;
      color: "red" | "yellow";
      nextTurn: "red" | "yellow";
      winner: string | null;
      scores: { p1: number; p2: number };
      board: Counter[][];
    }) => {
      dispatch(applyMove(data));
      setLastMove({ row: data.row, col: data.col });
      setCanMove(true);
    };

    const onGameRestarted = (roomState: RoomState) => {
      dispatch(applyGameRestarted(roomState));
      setLastMove(null);
    };

    const onTimerUpdate = ({ timer }: { timer: number }) => {
      dispatch(applyTimerUpdate(timer));
    };

    const onTurnTimeout = ({ nextTurn }: { nextTurn: "red" | "yellow" }) => {
      dispatch(applyTurnTimeout({ nextTurn }));
    };

    const onPlayerDisconnected = ({ roomState }: { roomState: RoomState }) => {
      dispatch(setRoomUpdated(roomState));
    };

    const onMatchPaused = ({ roomState }: { roomState: RoomState }) => {
      dispatch(setRoomUpdated(roomState));
    };

    const onMatchResumed = ({ roomState }: { roomState: RoomState }) => {
      dispatch(setRoomUpdated(roomState));
    };

    const onResumeError = ({ message }: { message: string }) => {
      dispatch(setError(message));
    };

    const onRematchVote = (data: {
      votes: { p1?: boolean; p2?: boolean };
      by: "p1" | "p2";
    }) => {
      dispatch(applyRematchVote(data));
    };
    
    const onResumeVote = (data: {
      votes: { p1?: boolean; p2?: boolean };
      by: "p1" | "p2";
    }) => {
      dispatch(applyResumeVote(data));
    };

    const onMatchForfeited = (data: {
      forfeitedBy: "p1" | "p2";
      winner: string | null;
      roomState: RoomState;
    }) => {
      dispatch(applyMatchForfeited(data));
    };

    const onError = ({ message }: { message: string }) => {
      dispatch(setError(message));
    };

    socket.on("room-updated", onRoomUpdated);
    socket.on("move-made", onMoveMade);
    socket.on("game-restarted", onGameRestarted);
    socket.on("timer-update", onTimerUpdate);
    socket.on("turn-timeout", onTurnTimeout);
    socket.on("player-disconnected", onPlayerDisconnected);
    socket.on("match-paused", onMatchPaused);
    socket.on("match-resumed", onMatchResumed);
    socket.on("resume-error", onResumeError);
    socket.on("resume-vote", onResumeVote);
    socket.on("rematch-vote", onRematchVote);
    socket.on("match-forfeited", onMatchForfeited);
    socket.on("error", onError);
    
    return () => {
      socket.off("room-updated", onRoomUpdated);
      socket.off("move-made", onMoveMade);
      socket.off("game-restarted", onGameRestarted);
      socket.off("timer-update", onTimerUpdate);
      socket.off("turn-timeout", onTurnTimeout);
      socket.off("player-disconnected", onPlayerDisconnected);
      socket.off("match-paused", onMatchPaused);
      socket.off("match-resumed", onMatchResumed);
      socket.off("resume-error", onResumeError);
      socket.off("resume-vote", onResumeVote);
      socket.off("rematch-vote", onRematchVote);
      socket.off("match-forfeited", onMatchForfeited);
      socket.off("error", onError);
    };

  }, [dispatch]);

  // Timer is now server-authoritative. Clients just listen for 'timer-update'.

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleColumnClick = useCallback(
    (col: number) => {
      if (!isMyTurn || !canMove || room?.winner || room?.isPaused) return;
      setCanMove(false);
      socket.emit("make-move", { col });
    },
    [isMyTurn, canMove, room?.winner, room?.isPaused],
  );

  const handlePauseToggle = () => {
    if (isSpectator || room?.winner || !room?.isStarted) return;
    if (room?.isPaused) {
      socket.emit("resume-match");
      return;
    }
    socket.emit("pause-match");
  };

  const handlePlayAgain = () => {
    // FIX C3: either player may now vote for a rematch; the server waits for BOTH votes.
    if (role !== "p1" && role !== "p2") return;
    if (!room?.winner) return;
    // Prevent re-emit once this client has already voted.
    if (role === "p1" && rematchVotes.p1) return;
    if (role === "p2" && rematchVotes.p2) return;
    socket.emit("play-again");
  };

  const handleQuit = () => {
    // FIX H1: send an explicit `quit-room` so the server knows this is
    // intentional. It forfeits the game (if mid-match) and tears the
    // socket's room state down cleanly before we disconnect.
    if (role === "p1" || role === "p2" || role === "spectator") {
      socket.emit("quit-room");
    }
    dispatch(resetMultiplayer());
    // Small delay lets the server process quit-room before we hop pages.
    setTimeout(() => {
      socket.disconnect();
      navigate("/");
    }, 50);
  };

  // ─────────────────────────────────────────────────────────────────────────

  if (reconnecting || !room) {
    return (
      <PageWrapper>
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <p style={{ color: "var(--color-white)", fontSize: "1.8rem" }}>
            {reconnecting ? "Reconnecting…" : "Loading…"}
          </p>
        </div>
      </PageWrapper>
    );
  }

  const p1 = room.players.find((p) => p.role === "p1");
  const p2 = room.players.find((p) => p.role === "p2");

  const getWinnerLabel = () => {
    if (!room.winner) return "";
    if (room.winner === "tie") return "It's a Draw!";
    const winnerPlayer = room.players.find((p) => p.role === room.winner);
    return `${winnerPlayer?.name ?? "Someone"} wins!`;
  };

  const myTurnPlayerName =
    room.turn === "red" ? (p1?.name ?? "Player 1") : (p2?.name ?? "Player 2");

  const handleShareWin = () => {
    const winnerLabel = getWinnerLabel();
    composeCast(
      `${winnerLabel} Connect Four · ${room.scores.p1}-${room.scores.p2} 🔴🟡`,
      APP_BASE_URL,
    );
  };

  const p1Card = (
    <PlayerCard color="red" isActive={room.turn === "red" && !room.winner}>
      <PlayerAvatar
        pfpUrl={p1?.pfpUrl}
        name={p1?.name ?? "P1"}
        size={44}
        ringColor="rgba(255,255,255,0.7)"
        isActive={room.turn === "red" && !room.winner}
      />
      <PlayerName color="red">
        {p1?.username ? `@${p1.username}` : (p1?.name ?? "Player 1")}
      </PlayerName>
      <PlayerScore color="red">{room.scores.p1}</PlayerScore>
    </PlayerCard>
  );

  const p2Card = (
    <PlayerCard
      color="yellow"
      isActive={room.turn === "yellow" && !room.winner}
    >
      <PlayerAvatar
        pfpUrl={p2?.pfpUrl}
        name={p2?.name ?? "P2"}
        size={44}
        ringColor="rgba(0,0,0,0.25)"
        isActive={room.turn === "yellow" && !room.winner}
      />
      <PlayerName color="yellow">
        {p2?.username ? `@${p2.username}` : (p2?.name ?? "Player 2")}
      </PlayerName>
      <PlayerScore color="yellow">{room.scores.p2}</PlayerScore>
    </PlayerCard>
  );

  return (
    <PageWrapper>
      {/* ── Header ── */}
      <Header>
        <QuitBtn onClick={handleQuit}>Quit</QuitBtn>
        <RoomInfo roomId={room.id} />
        {!isSpectator && room.isStarted && !room.winner && (
          <PauseBtn onClick={handlePauseToggle}>
            {isPaused ? "Resume" : "Pause"}
          </PauseBtn>
        )}
        <VoiceChat {...voiceChat} />
      </Header>

      {/* ── Main grid (matches CPU layout: P1 | Board | P2) ── */}
      <GameArea>
        {/* Desktop: player 1 card in left column */}
        <P1Slot>{p1Card}</P1Slot>

        {/* Mobile only: both player cards above the board */}
        <MobilePlayersRow>
          {p1Card}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "0.4rem",
            }}
          >
            <TimerBadge low={room.timer <= 5}>
              {room.winner ? "—" : room.timer}s
            </TimerBadge>
            {!room.winner && room.isStarted && (
              <TurnIndicator>
                {isPaused
                  ? "Match paused"
                  : isMyTurn
                    ? "🟢 Your turn"
                    : `${myTurnPlayerName}'s turn`}
              </TurnIndicator>
            )}
          </div>
          {p2Card}
        </MobilePlayersRow>

        {/* Board */}
        <BoardSlot>
          <BoardOuter>
            {windowWidth > 760 ? (
              <WhiteLayoutLarge className="board-white" />
            ) : (
              <WhiteLayoutSmall className="board-white" />
            )}

            {/* Counter grid */}
            <CounterGridOverlay>
              {room.board.map((row, rowIdx) =>
                row.map((cell, colIdx) => {
                  const isNew =
                    !!cell &&
                    lastMove?.row === rowIdx &&
                    lastMove?.col === colIdx;
                  return (
                    <CounterCell key={`${rowIdx}-${colIdx}`}>
                      <AnimatePresence>
                        {cell && (
                          <CounterImg
                            key={`${rowIdx}-${colIdx}-${cell}`}
                            $src={counterImages[cell][size]}
                            initial={{ y: isNew ? -(80 * rowIdx + 70) : 0 }}
                            animate={
                              isNew
                                ? {
                                    y: [-(80 * rowIdx + 70), 0, -50, 0, -20, 0],
                                  }
                                : { y: 0 }
                            }
                            transition={
                              isNew
                                ? {
                                    duration: 0.4,
                                    times: [0, 0.4, 0.6, 0.7, 0.8, 1],
                                  }
                                : { duration: 0 }
                            }
                          />
                        )}
                      </AnimatePresence>
                    </CounterCell>
                  );
                }),
              )}
            </CounterGridOverlay>

            {/* Column click overlay */}
            <ControlOverlay>
              {Array(7)
                .fill(null)
                .map((_, col) => (
                  <ColumnBtn
                    key={col}
                    canPlay={!!isMyTurn && !room.winner && !room.isPaused}
                    onMouseEnter={() => setHoveredCol(col)}
                    onMouseLeave={() => setHoveredCol(null)}
                    onClick={() => handleColumnClick(col)}
                  >
                    {isMyTurn &&
                      hoveredCol === col &&
                      !room.winner &&
                      !room.isPaused &&
                      myColor && (
                        <Pointer
                          color={myColor}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        />
                      )}
                  </ColumnBtn>
                ))}
            </ControlOverlay>

            {windowWidth > 760 ? (
              <BlackLayoutLarge className="board-black" />
            ) : (
              <BlackLayoutSmall className="board-black" />
            )}
          </BoardOuter>
        </BoardSlot>

        {/* Desktop: player 2 card in right column */}
        <P2Slot>{p2Card}</P2Slot>

        {/* Info row: timer + turn indicator (desktop only; mobile shows in MobilePlayersRow) */}
        <InfoSlot>
          {!isConnected && <ErrorMsg>Connection lost. Reconnecting…</ErrorMsg>}

          {/* Desktop timer & turn indicator */}
          <DesktopTimerRow>
            <TimerBadge low={room.timer <= 5}>
              {room.winner ? "—" : room.timer}s
            </TimerBadge>
            {!room.winner && room.isStarted && (
              <TurnIndicator>
                {isPaused
                  ? "Match paused"
                  : isMyTurn
                    ? "🟢 Your turn"
                    : `${myTurnPlayerName}'s turn`}
              </TurnIndicator>
            )}
          </DesktopTimerRow>

          {/* Waiting / spectator banners */}
          {!room.isStarted && role === "p1" && (
            <WaitingBanner>
              Waiting for opponent… &nbsp;
              <ShareInviteBtn
                roomId={room.id}
                hostName={
                  farcasterUser?.displayName ||
                  farcasterUser?.username ||
                  p1?.name
                }
                variant="small"
              />
            </WaitingBanner>
          )}
          {isSpectator && (
            <SpectatorBanner>
              👁 You are watching as a spectator
            </SpectatorBanner>
          )}
          {room.isPaused && (
            <PauseBanner>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                <div>Match paused{room.pausedBy ? ` by ${room.pausedBy.toUpperCase()}` : ""}.</div>
                {!isSpectator && room.players.length === 2 && (
                  <div style={{ fontSize: "1.2rem", color: "rgba(255,255,255,0.8)" }}>
                    {(() => {
                      const myVote = room.resumeVotes?.[role as "p1" | "p2"];
                      const oppRole = role === "p1" ? "p2" : "p1";
                      const opponentVote = room.resumeVotes?.[oppRole];
                      
                      if (myVote && !opponentVote) return "You are ready. Waiting for opponent…";
                      if (!myVote && opponentVote) return "Opponent is ready to resume!";
                      return "Both players must click Resume to continue.";
                    })()}
                  </div>
                )}
              </div>
            </PauseBanner>
          )}

          {/* Result overlay */}
          <AnimatePresence>
            {room.winner && (
              <ResultOverlay
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
              >
                <ResultTitle>
                  {room.forfeited ? "Match forfeited" : "Game over"}
                </ResultTitle>
                <ResultWinner>{getWinnerLabel()}</ResultWinner>
                {/* FIX C3: both players can now vote to rematch. */}
                {role === "p1" || role === "p2" ? (
                  (() => {
                    const myVoted =
                      (role === "p1" && rematchVotes.p1) ||
                      (role === "p2" && rematchVotes.p2);
                    const opponentVoted =
                      (role === "p1" && rematchVotes.p2) ||
                      (role === "p2" && rematchVotes.p1);
                    return (
                      <>
                        <PlayAgainBtn
                          onClick={handlePlayAgain}
                          disabled={!!myVoted}
                          style={
                            myVoted ? { opacity: 0.6, cursor: "default" } : undefined
                          }
                        >
                          {myVoted ? "✓ Ready for rematch" : "Play Again"}
                        </PlayAgainBtn>
                        {myVoted && !opponentVoted && (
                          <p
                            style={{
                              fontSize: "1.3rem",
                              color: "rgba(0,0,0,0.55)",
                            }}
                          >
                            Waiting for opponent to accept…
                          </p>
                        )}
                        {!myVoted && opponentVoted && (
                          <p
                            style={{
                              fontSize: "1.3rem",
                              color: "var(--color-dark-purple)",
                            }}
                          >
                            Opponent wants a rematch!
                          </p>
                        )}
                      </>
                    );
                  })()
                ) : (
                  <p style={{ fontSize: "1.4rem", color: "rgba(0,0,0,0.5)" }}>
                    Waiting for players to restart…
                  </p>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: "0.8rem",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    marginTop: "0.4rem",
                  }}
                >
                  <button
                    onClick={handleShareWin}
                    style={{
                      background: "linear-gradient(135deg,#7945ff,#5c2dd5)",
                      border: "2px solid black",
                      borderRadius: "1rem",
                      padding: "0.6rem 1.4rem",
                      color: "white",
                      fontFamily: "inherit",
                      fontWeight: "bold",
                      fontSize: "1.3rem",
                      cursor: "pointer",
                      textTransform: "uppercase",
                    }}
                  >
                    ⬡ Share Result
                  </button>
                  <QuitBtn onClick={handleQuit} style={{ marginTop: 0 }}>
                    Leave Room
                  </QuitBtn>
                </div>
              </ResultOverlay>
            )}
          </AnimatePresence>
        </InfoSlot>

        {/* Extras: spectators */}
        <ExtrasSlot>
          <SpectatorList spectators={room.spectators} />
        </ExtrasSlot>
      </GameArea>
    </PageWrapper>
  );
};

export default MultiplayerGame;
